import React, { useState, useEffect } from 'react';
import { collection, query, onSnapshot, addDoc, updateDoc, doc, deleteDoc, getDocs, where, documentId } from 'firebase/firestore';
import { PlusCircle, Edit, Trash2, UserPlus, BookOpen, Loader2, LinkIcon, GraduationCap, Book } from 'lucide-react';

const EnseignantsManagement = ({ setMessage, setError, db, schoolId, userRole, isFrancoArabe = false }) => {
    const [selectedTab, setSelectedTab] = useState('newTeacher');
    // 'newTeacher', 'classAssignment', 'subjectAssignment'

    // States for "Nouveau Prof" tab
    const [enseignants, setEnseignants] = useState([]);
    const [newEnseignantNom, setNewEnseignantNom] = useState('');
    const [newEnseignantPrenom, setNewEnseignantPrenom] = useState('');
    const [newEnseignantEmail, setNewEnseignantEmail] = useState('');
    const [newEnseignantSexe, setNewEnseignantSexe] = useState('');
    const [newEnseignantTelephone, setNewEnseignantTelephone] = useState('');
    const [newEnseignantAdresse, setNewEnseignantAdresse] = useState('');
    const [newEnseignantTypeContrat, setNewEnseignantTypeContrat] = useState('');
    const [newEnseignantDuree, setNewEnseignantDuree] = useState('');
    const [newEnseignantTauxSalaire, setNewEnseignantTauxSalaire] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [currentEnseignant, setCurrentEnseignant] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [newEnseignantAuthUid, setNewEnseignantAuthUid] = useState(''); // Nouveau champ pour l'UID d'authentification

    // States for "Affectation Classes" tab
    const [classes, setClasses] = useState([]);
    const [academicYears, setAcademicYears] = useState([]);
    const [teacherClassAssignments, setTeacherClassAssignments] = useState([]);
    const [selectedTeacherForAssignment, setSelectedTeacherForAssignment] = useState('');
    const [selectedClassForAssignment, setSelectedClassForAssignment] = useState('');
    const [selectedYearForAssignment, setSelectedYearForAssignment] = useState('');
    const [isEditingAssignment, setIsEditingAssignment] = useState(false);
    const [currentAssignment, setCurrentAssignment] = useState(null);

    // States for "Matières Enseignées" tab
    const [subjects, setSubjects] = useState([]);
    const [teacherSubjectAssignments, setTeacherSubjectAssignments] = useState([]);
    const [selectedTeacherForSubjectAssignment, setSelectedTeacherForSubjectAssignment] = useState('');
    const [selectedSubjectForAssignment, setSelectedSubjectForAssignment] = useState('');
    const [selectedYearForSubjectAssignment, setSelectedYearForSubjectAssignment] = useState('');
    const [isEditingSubjectAssignment, setIsEditingSubjectAssignment] = useState(false);
    const [currentSubjectAssignment, setCurrentSubjectAssignment] = useState(null);

    // Fonction utilitaire pour filtrer les matières selon le type d'école
    const filterSubjectsBySchoolType = (subjectsList) => {
        if (!isFrancoArabe) {
            return subjectsList.filter(subject => 
                subject.type !== 'arabe' && (subject.status === 'actif' || !subject.status)
            );
        }
        return subjectsList.filter(subject => 
            subject.status === 'actif' || !subject.status
        );
    };

    // Fonction pour mettre à jour class_matieres
    const updateTeacherInClassMatieres = async (teacherId, subjectId, addTeacher = true) => {
        try {
            // Trouver tous les class_matieres qui ont cette matière
            const q = query(
                collection(db, 'schools', schoolId, 'class_matieres'),
                where('matiereId', '==', subjectId)
            );
            const snapshot = await getDocs(q);
            
            if (!snapshot.empty) {
                const batchPromises = snapshot.docs.map(doc => {
                    const data = doc.data();
                    let assignedTeacherIds = data.assignedTeacherIds || [];
                    
                    if (addTeacher) {
                        // Ajouter l'enseignant s'il n'est pas déjà présent
                        if (!assignedTeacherIds.includes(teacherId)) {
                            assignedTeacherIds = [...assignedTeacherIds, teacherId];
                        }
                    } else {
                        // Retirer l'enseignant
                        assignedTeacherIds = assignedTeacherIds.filter(id => id !== teacherId);
                    }
                    
                    return updateDoc(doc.ref, {
                        assignedTeacherIds: assignedTeacherIds,
                        updatedAt: new Date()
                    });
                });
                
                await Promise.all(batchPromises);
            }
        } catch (error) {
            console.error("Error updating class_matieres:", error);
            throw error;
        }
    };

    // --- Data Fetching ---
    useEffect(() => {
        if (!db) {
            setError('Base de données Firestore non disponible.');
            setIsLoading(false);
            return;
        }

        setError('');
        setMessage('');

        if (!schoolId) {
            setEnseignants([]);
            setClasses([]);
            setTeacherClassAssignments([]);
            setSubjects([]);
            setTeacherSubjectAssignments([]);
            setMessage("Veuillez sélectionner une école pour gérer les enseignants et leurs affectations.");
            setIsLoading(false);
            return;
        }

        // Fetch Enseignants
        const enseignantsCollectionRef = collection(db, 'schools', schoolId, 'enseignants');
        const unsubscribeEnseignants = onSnapshot(query(enseignantsCollectionRef), (snapshot) => {
            const fetchedEnseignants = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setEnseignants(fetchedEnseignants);
            setIsLoading(false);
        }, (err) => {
            console.error("Error fetching enseignants:", err);
            setError("Erreur lors du chargement des enseignants.");
            setIsLoading(false);
        });

        // Fetch Classes
        const classesCollectionRef = collection(db, 'schools', schoolId, 'classes');
        const unsubscribeClasses = onSnapshot(query(classesCollectionRef), (snapshot) => {
            const fetchedClasses = snapshot.docs.map(doc => ({ id: doc.id, name: doc.data().nom, ...doc.data() }));
            setClasses(fetchedClasses);
        }, (err) => {
            console.error("Error fetching classes:", err);
            setError("Erreur lors du chargement des classes.");
        });
        
        // Fetch Teacher Class Assignments
        const assignmentsCollectionRef = collection(db, 'schools', schoolId, 'teacherClassAssignments');
        const unsubscribeAssignments = onSnapshot(query(assignmentsCollectionRef), (snapshot) => {
            const fetchedAssignments = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setTeacherClassAssignments(fetchedAssignments);
        }, (err) => {
            console.error("Error fetching teacher class assignments:", err);
            setError("Erreur lors du chargement des affectations de classes.");
        });
        
        // Fetch Subjects from matieres_globales (compatible with MatieresManagement)
        const subjectsCollectionRef = collection(db, 'schools', schoolId, 'matieres_globales');
        const unsubscribeSubjects = onSnapshot(query(subjectsCollectionRef), (snapshot) => {
            const fetchedSubjects = snapshot.docs.map(doc => { 
                const data = doc.data();
                return { 
                    id: doc.id, 
                    name: data.name || data.nom || 'Matière sans nom',
                    description: data.description || '',
                    niveau: data.niveau || '',
                    type: data.type || 'français',
                    status: data.status || 'actif',
                    ...data 
                };
            });
            
            // Filtrer pour ne garder que les matières actives et selon le type d'école
            let activeSubjects = fetchedSubjects.filter(subject => 
                subject.status === 'actif' || subject.status === undefined
            );
            
            // Appliquer le filtre selon le type d'école
            activeSubjects = filterSubjectsBySchoolType(activeSubjects);
            
            setSubjects(activeSubjects);
        }, (err) => {
            console.error("Error fetching subjects from matieres_globales:", err);
            setError("Erreur lors du chargement des matières.");
        });
        
        // Fetch Teacher Subject Assignments
        const teacherSubjectAssignmentsCollectionRef = collection(db, 'schools', schoolId, 'teacherSubjectAssignments');
        const unsubscribeTeacherSubjectAssignments = onSnapshot(query(teacherSubjectAssignmentsCollectionRef), (snapshot) => {
            const fetchedAssignments = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setTeacherSubjectAssignments(fetchedAssignments);
        }, (err) => {
            console.error("Error fetching teacher subject assignments:", err);
            setError("Erreur lors du chargement des affectations de matières.");
        });
        
        // Generate academic years
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let i = -1; i <= 1; i++) {
            years.push(`${currentYear + i}-${currentYear + i + 1}`)
        }
        setAcademicYears(years);

        return () => {
            unsubscribeEnseignants();
            unsubscribeClasses();
            unsubscribeAssignments();
            unsubscribeSubjects();
            unsubscribeTeacherSubjectAssignments();
        };
    }, [db, schoolId, setMessage, setError, isFrancoArabe]);

    // --- "Nouveau Prof" Tab Logic ---
    const resetTeacherForm = () => {
        setNewEnseignantNom('');
        setNewEnseignantPrenom('');
        setNewEnseignantEmail('');
        setNewEnseignantSexe('');
        setNewEnseignantTelephone('');
        setNewEnseignantAdresse('');
        setNewEnseignantTypeContrat('');
        setNewEnseignantDuree('');
        setNewEnseignantTauxSalaire('');
        setNewEnseignantAuthUid('');
        setIsEditing(false);
        setCurrentEnseignant(null);
    };

    const handleTeacherSubmit = async (e) => {
        e.preventDefault();
        if (!newEnseignantNom.trim() || !newEnseignantPrenom.trim() || !newEnseignantEmail.trim()) {
            setError('Veuillez remplir les champs Nom, Prénom et Email.');
            return;
        }

        setMessage('');
        setError('');
        try {
            const enseignantData = {
                nom: newEnseignantNom.trim(),
                prenom: newEnseignantPrenom.trim(),
                email: newEnseignantEmail.trim(),
                sexe: newEnseignantSexe,
                telephone: newEnseignantTelephone.trim(),
                adresse: newEnseignantAdresse.trim(),
                typeContrat: newEnseignantTypeContrat,
                dureeContrat: newEnseignantDuree ? parseInt(newEnseignantDuree, 10) : null,
                tauxSalaire: newEnseignantTauxSalaire ? parseFloat(newEnseignantTauxSalaire) : null,
                userId: newEnseignantAuthUid.trim(),
            };
            
            if (isEditing && currentEnseignant) {
                // Pour la mise à jour, assurez-vous que l'email n'est pas déjà pris par un autre enseignant
                const q = query(collection(db, 'schools', schoolId, 'enseignants'), where('email', '==', newEnseignantEmail.trim()), where(documentId(), '!=', currentEnseignant.id));
                const querySnapshot = await getDocs(q);
                if (!querySnapshot.empty) {
                    setError(`Un autre enseignant avec l'adresse e-mail "${newEnseignantEmail.trim()}" existe déjà.`);
                    return;
                }
                
                // Vérifier l'unicité de l'UID lors de la modification
                if (newEnseignantAuthUid.trim() && newEnseignantAuthUid.trim() !== (currentEnseignant.userId || '')) {
                    const uidQ = query(collection(db, 'schools', schoolId, 'enseignants'), where('userId', '==', newEnseignantAuthUid.trim()), where(documentId(), '!=', currentEnseignant.id));
                    const uidSnapshot = await getDocs(uidQ);
                    if (!uidSnapshot.empty) {
                        setError(`L'UID d'authentification "${newEnseignantAuthUid.trim()}" est déjà associé à un autre enseignant.`);
                        return;
                    }
                }

                await updateDoc(doc(db, 'schools', schoolId, 'enseignants', currentEnseignant.id), enseignantData);
                setMessage('Enseignant mis à jour avec succès !');
            } else {
                // Check for duplicate email
                const q = query(collection(db, 'schools', schoolId, 'enseignants'), where('email', '==', newEnseignantEmail.trim()));
                const querySnapshot = await getDocs(q);
                if (!querySnapshot.empty) {
                    setError(`Un enseignant avec l'adresse e-mail "${newEnseignantEmail.trim()}" existe déjà.`);
                    return;
                }
                
                // Vérifier l'unicité de l'UID
                if (newEnseignantAuthUid.trim()) {
                    const uidQ = query(collection(db, 'schools', schoolId, 'enseignants'), where('userId', '==', newEnseignantAuthUid.trim()));
                    const uidSnapshot = await getDocs(uidQ);
                    if (!uidSnapshot.empty) {
                        setError(`Un enseignant avec l'UID d'authentification "${newEnseignantAuthUid.trim()}" existe déjà.`);
                        return;
                    }
                }

                await addDoc(collection(db, 'schools', schoolId, 'enseignants'), enseignantData);
                setMessage('Enseignant ajouté avec succès !');
            }
            resetTeacherForm();
        } catch (e) {
            console.error("Error adding/updating enseignant:", e);
            setError(`Erreur lors de l'ajout/mise à jour de l'enseignant : ${e.message}`);
        }
    };

    const handleEditTeacher = (enseignant) => {
        setIsEditing(true);
        setCurrentEnseignant(enseignant);
        setNewEnseignantNom(enseignant.nom);
        setNewEnseignantPrenom(enseignant.prenom);
        setNewEnseignantEmail(enseignant.email);
        setNewEnseignantSexe(enseignant.sexe || '');
        setNewEnseignantTelephone(enseignant.telephone || '');
        setNewEnseignantAdresse(enseignant.adresse || '');
        setNewEnseignantTypeContrat(enseignant.typeContrat || '');
        setNewEnseignantDuree(enseignant.dureeContrat ? enseignant.dureeContrat.toString() : '');
        setNewEnseignantTauxSalaire(enseignant.tauxSalaire ? enseignant.tauxSalaire.toString() : '');
        setNewEnseignantAuthUid(enseignant.userId || '');
    };

    const handleDeleteTeacher = async (enseignantId) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer cet enseignant ? Cette action est irréversible.')) {
            setMessage('');
            setError('');
            try {
                await deleteDoc(doc(db, 'schools', schoolId, 'enseignants', enseignantId));
                setMessage('Enseignant supprimé avec succès !');
            } catch (e) {
                console.error("Error deleting enseignant:", e);
                setError(`Erreur lors de la suppression de l'enseignant : ${e.message}`);
            }
        }
    };

    // --- "Affectation Classes" Tab Logic ---
    const resetAssignmentForm = () => {
        setSelectedTeacherForAssignment('');
        setSelectedClassForAssignment('');
        setSelectedYearForAssignment('');
        setIsEditingAssignment(false);
        setCurrentAssignment(null);
    };

    const handleAssignmentSubmit = async (e) => {
        e.preventDefault();
        if (!selectedTeacherForAssignment || !selectedClassForAssignment || !selectedYearForAssignment) {
            setError('Veuillez sélectionner un enseignant, une classe et une année scolaire pour l\'affectation.');
            return;
        }

        setMessage('');
        setError('');
        try {
            const assignmentData = {
                teacherId: selectedTeacherForAssignment,
                classId: selectedClassForAssignment,
                academicYear: selectedYearForAssignment,
                createdAt: new Date(),
            };
            
            if (isEditingAssignment && currentAssignment) {
                await updateDoc(doc(db, 'schools', schoolId, 'teacherClassAssignments', currentAssignment.id), assignmentData);
                setMessage('Affectation de classe mise à jour avec succès !');
            } else {
                // Check for duplicate assignment
                const q = query(
                    collection(db, 'schools', schoolId, 'teacherClassAssignments'),
                    where('teacherId', '==', selectedTeacherForAssignment),
                    where('classId', '==', selectedClassForAssignment),
                    where('academicYear', '==', selectedYearForAssignment)
                );
                const querySnapshot = await getDocs(q);
                if (!querySnapshot.empty) {
                    setError('Cette affectation de classe existe déjà pour cet enseignant et cette année.');
                    return;
                }

                await addDoc(collection(db, 'schools', schoolId, 'teacherClassAssignments'), assignmentData);
                setMessage('Affectation de classe ajoutée avec succès !');
            }
            resetAssignmentForm();
        } catch (e) {
            console.error("Error adding/updating class assignment:", e);
            setError(`Erreur lors de l'ajout/mise à jour de l'affectation de classe : ${e.message}`);
        }
    };

    const handleEditAssignment = (assignment) => {
        setIsEditingAssignment(true);
        setCurrentAssignment(assignment);
        setSelectedTeacherForAssignment(assignment.teacherId);
        setSelectedClassForAssignment(assignment.classId);
        setSelectedYearForAssignment(assignment.academicYear);
    };

    const handleDeleteAssignment = async (assignmentId) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer cette affectation de classe ? Cette action est irréversible.')) {
            setMessage('');
            setError('');
            try {
                await deleteDoc(doc(db, 'schools', schoolId, 'teacherClassAssignments', assignmentId));
                setMessage('Affectation de classe supprimée avec succès !');
            } catch (e) {
                console.error("Error deleting class assignment:", e);
                setError(`Erreur lors de la suppression de l'affectation de classe : ${e.message}`);
            }
        }
    };

    // --- "Matières Enseignées" Tab Logic ---
    const resetSubjectAssignmentForm = () => {
        setSelectedTeacherForSubjectAssignment('');
        setSelectedSubjectForAssignment('');
        setSelectedYearForSubjectAssignment('');
        setIsEditingSubjectAssignment(false);
        setCurrentSubjectAssignment(null);
    };

    const handleSubjectAssignmentSubmit = async (e) => {
        e.preventDefault();
        if (!selectedTeacherForSubjectAssignment || !selectedSubjectForAssignment || !selectedYearForSubjectAssignment) {
            setError('Veuillez sélectionner un enseignant, une matière et une année scolaire pour l\'affectation.');
            return;
        }

        setMessage('');
        setError('');
        
        try {
            // Récupérer les informations de la matière
            const subject = subjects.find(s => s.id === selectedSubjectForAssignment);
            if (!subject) {
                setError('Matière non trouvée.');
                return;
            }

            const assignmentData = {
                teacherId: selectedTeacherForSubjectAssignment,
                subjectId: selectedSubjectForAssignment,
                subjectName: subject.name || subject.nom,
                academicYear: selectedYearForSubjectAssignment,
                createdAt: new Date(),
            };

            if (isEditingSubjectAssignment && currentSubjectAssignment) {
                // Mise à jour de l'affectation
                await updateDoc(doc(db, 'schools', schoolId, 'teacherSubjectAssignments', currentSubjectAssignment.id), assignmentData);
                
                // Mettre à jour aussi dans class_matieres si nécessaire
                await updateTeacherInClassMatieres(
                    selectedTeacherForSubjectAssignment, 
                    selectedSubjectForAssignment, 
                    true // add teacher
                );
                
                setMessage('Affectation de matière mise à jour avec succès !');
            } else {
                // Vérifier l'unicité de l'affectation
                const q = query(
                    collection(db, 'schools', schoolId, 'teacherSubjectAssignments'),
                    where('teacherId', '==', selectedTeacherForSubjectAssignment),
                    where('subjectId', '==', selectedSubjectForAssignment),
                    where('academicYear', '==', selectedYearForSubjectAssignment)
                );
                const querySnapshot = await getDocs(q);
                if (!querySnapshot.empty) {
                    setError('Cette affectation de matière existe déjà pour cet enseignant et cette année.');
                    return;
                }

                // Ajouter l'affectation
                await addDoc(collection(db, 'schools', schoolId, 'teacherSubjectAssignments'), assignmentData);
                
                // Ajouter l'enseignant aux matières dans class_matieres
                await updateTeacherInClassMatieres(
                    selectedTeacherForSubjectAssignment, 
                    selectedSubjectForAssignment, 
                    true // add teacher
                );
                
                setMessage('Affectation de matière ajoutée avec succès !');
            }
            resetSubjectAssignmentForm();
        } catch (e) {
            console.error("Error adding/updating subject assignment:", e);
            setError(`Erreur lors de l'ajout/mise à jour de l'affectation de matière : ${e.message}`);
        }
    };

    const handleEditSubjectAssignment = (assignment) => {
        setIsEditingSubjectAssignment(true);
        setCurrentSubjectAssignment(assignment);
        setSelectedTeacherForSubjectAssignment(assignment.teacherId);
        setSelectedSubjectForAssignment(assignment.subjectId);
        setSelectedYearForSubjectAssignment(assignment.academicYear);
    };

    const handleDeleteSubjectAssignment = async (assignmentId) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer cette affectation de matière ? Cette action est irréversible.')) {
            setMessage('');
            setError('');
            try {
                // Récupérer l'affectation avant suppression pour avoir les infos
                const assignment = teacherSubjectAssignments.find(a => a.id === assignmentId);
                
                await deleteDoc(doc(db, 'schools', schoolId, 'teacherSubjectAssignments', assignmentId));
                
                // Retirer l'enseignant de class_matieres
                if (assignment) {
                    await updateTeacherInClassMatieres(
                        assignment.teacherId, 
                        assignment.subjectId, 
                        false // remove teacher
                    );
                }
                
                setMessage('Affectation de matière supprimée avec succès !');
            } catch (e) {
                console.error("Error deleting subject assignment:", e);
                setError(`Erreur lors de la suppression de l'affectation de matière : ${e.message}`);
            }
        }
    };

    // Helper to get teacher name by ID
    const getTeacherName = (teacherId) => {
        const teacher = enseignants.find(t => t.id === teacherId);
        return teacher ? `${teacher.nom} ${teacher.prenom}` : 'Enseignant Inconnu';
    };

    // Helper to get class name by ID
    const getClassName = (classId) => {
        const classe = classes.find(c => c.id === classId);
        return classe ? classe.name : 'Classe Inconnue';
    };

    // Helper to get subject name by ID
    const getSubjectName = (subjectId) => {
        const subject = subjects.find(s => s.id === subjectId);
        if (!subject) return 'Matière Inconnue';
        return subject.name || subject.nom || 'Matière sans nom';
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Loader2 className="animate-spin text-blue-500" size={48} />
                <p className="ml-3 text-lg text-gray-700">Chargement des données...</p>
            </div>
        );
    }

    if (userRole === 'superAdmin' && !schoolId) {
        return (
            <div className="p-4 bg-gray-50 rounded-lg shadow-md">
                <h2 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
                    <GraduationCap size={28} className="mr-3 text-blue-600" /> Gestion des Enseignants
                </h2>
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 mb-4">
                    <p className="font-semibold">Veuillez sélectionner une école dans le tableau de bord pour gérer les enseignants.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 bg-gray-50 rounded-lg shadow-md">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
                <GraduationCap size={28} className="mr-3 text-blue-600" /> Gestion des Enseignants
            </h2>

            {/* Badge type d'école */}
            {isFrancoArabe !== undefined && (
                <div className="mb-4">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${isFrancoArabe ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                        {isFrancoArabe ? '🏫 École Franco-Arabe' : '🏫 École Standard'}
                    </span>
                </div>
            )}

            {/* Tab Navigation */}
            <div className="mb-6 border-b border-gray-200">
                <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                    <button
                        onClick={() => setSelectedTab('newTeacher')}
                        className={`
                            ${selectedTab === 'newTeacher'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                            whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center
                        `}
                    >
                        <UserPlus size={18} className="mr-2" /> Nouveau Prof
                    </button>
                    <button
                        onClick={() => setSelectedTab('classAssignment')}
                        className={`
                            ${selectedTab === 'classAssignment'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                            whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center
                        `}
                    >
                        <LinkIcon size={18} className="mr-2" /> Affectation Classes
                    </button>
                    <button
                        onClick={() => setSelectedTab('subjectAssignment')}
                        className={`
                            ${selectedTab === 'subjectAssignment'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                            whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center
                        `}
                    >
                        <Book size={18} className="mr-2" /> Matières Enseignées
                    </button>
                </nav>
            </div>

            {/* Tab Content */}
            <div>
                {selectedTab === 'newTeacher' && (
                    <div className="bg-white p-6 rounded-lg shadow-lg mb-8">
                        <h3 className="text-2xl font-semibold text-gray-800 mb-6">
                            {isEditing ? 'Modifier un Enseignant' : 'Ajouter un Nouvel Enseignant'}
                        </h3>
                        <form onSubmit={handleTeacherSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="nom" className="block text-sm font-medium text-gray-700">Nom</label>
                                    <input
                                        type="text"
                                        id="nom"
                                        value={newEnseignantNom}
                                        onChange={(e) => setNewEnseignantNom(e.target.value)}
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Nom de famille"
                                        required
                                    />
                                </div>
                                <div>
                                    <label htmlFor="prenom" className="block text-sm font-medium text-gray-700">Prénom</label>
                                    <input
                                        type="text"
                                        id="prenom"
                                        value={newEnseignantPrenom}
                                        onChange={(e) => setNewEnseignantPrenom(e.target.value)}
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Prénom"
                                        required
                                    />
                                </div>
                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
                                    <input
                                        type="email"
                                        id="email"
                                        value={newEnseignantEmail}
                                        onChange={(e) => setNewEnseignantEmail(e.target.value)}
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="email@example.com"
                                        required
                                    />
                                </div>
                                <div>
                                    <label htmlFor="teacherAuthUid" className="block text-sm font-medium text-gray-700">UID Authentification (Firebase)</label>
                                    <input
                                        type="text"
                                        id="teacherAuthUid"
                                        value={newEnseignantAuthUid}
                                        onChange={(e) => setNewEnseignantAuthUid(e.target.value)}
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="UID de l'enseignant (ex: abcXYZ123)"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="sexe" className="block text-sm font-medium text-gray-700">Sexe</label>
                                    <select
                                        id="sexe"
                                        value={newEnseignantSexe}
                                        onChange={(e) => setNewEnseignantSexe(e.target.value)}
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="">Sélectionner</option>
                                        <option value="Homme">Homme</option>
                                        <option value="Femme">Femme</option>
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="telephone" className="block text-sm font-medium text-gray-700">Téléphone</label>
                                    <input
                                        type="text"
                                        id="telephone"
                                        value={newEnseignantTelephone}
                                        onChange={(e) => setNewEnseignantTelephone(e.target.value)}
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Ex: +221 77 123 45 67"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="adresse" className="block text-sm font-medium text-gray-700">Adresse</label>
                                    <input
                                        type="text"
                                        id="adresse"
                                        value={newEnseignantAdresse}
                                        onChange={(e) => setNewEnseignantAdresse(e.target.value)}
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Adresse complète"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="typeContrat" className="block text-sm font-medium text-gray-700">Type de Contrat</label>
                                    <select
                                        id="typeContrat"
                                        value={newEnseignantTypeContrat}
                                        onChange={(e) => setNewEnseignantTypeContrat(e.target.value)}
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="">Sélectionner</option>
                                        <option value="CDI">CDI</option>
                                        <option value="CDD">CDD</option>
                                        <option value="Vacataire">Vacataire</option>
                                        <option value="Autre">Autre</option>
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="dureeContrat" className="block text-sm font-medium text-gray-700">Durée du Contrat (en mois/ans si applicable)</label>
                                    <input
                                        type="number"
                                        id="dureeContrat"
                                        value={newEnseignantDuree}
                                        onChange={(e) => setNewEnseignantDuree(e.target.value)}
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Ex: 12 (mois), 3 (ans)"
                                        min="0"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="tauxSalaire" className="block text-sm font-medium text-gray-700">Taux Horaire / Salaire (XOF)</label>
                                    <input
                                        type="number"
                                        id="tauxSalaire"
                                        value={newEnseignantTauxSalaire}
                                        onChange={(e) => setNewEnseignantTauxSalaire(e.target.value)}
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Ex: 5000 (XOF/heure) ou 250000 (XOF/mois)"
                                        min="0"
                                    />
                                </div>
                            </div>

                            <div className="flex justify-end space-x-3">
                                {isEditing && (
                                    <button
                                        type="button"
                                        onClick={resetTeacherForm}
                                        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200"
                                    >
                                        Annuler
                                    </button>
                                )}
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                >
                                    {isEditing ? 'Mettre à jour l\'Enseignant' : 'Ajouter l\'Enseignant'}
                                </button>
                            </div>
                        </form>

                        <h3 className="text-xl font-semibold text-gray-700 mb-4 mt-8">Liste des Enseignants ({enseignants.length})</h3>
                        {enseignants.length > 0 ? (
                            <div className="overflow-x-auto bg-white rounded-lg shadow-md">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prénom</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">UID Auth.</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sexe</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Téléphone</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type Contrat</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Durée</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Taux/Salaire</th>
                                            {(userRole === 'director' || userRole === 'admin' || userRole === 'superAdmin') && (
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {enseignants.map(enseignant => (
                                            <tr key={enseignant.id}>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{enseignant.nom}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{enseignant.prenom}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{enseignant.email}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono text-xs">{enseignant.userId || 'N/A'}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{enseignant.sexe || 'N/A'}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{enseignant.telephone || 'N/A'}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{enseignant.typeContrat || 'N/A'}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{enseignant.dureeContrat !== null ? enseignant.dureeContrat : 'N/A'}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{enseignant.tauxSalaire !== null ? `${enseignant.tauxSalaire} XOF` : 'N/A'}</td>
                                                {(userRole === 'director' || userRole === 'admin' || userRole === 'superAdmin') && (
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                        <div className="flex space-x-2">
                                                            <button
                                                                onClick={() => handleEditTeacher(enseignant)}
                                                                className="text-indigo-600 hover:text-indigo-900 p-2 rounded-full hover:bg-indigo-100"
                                                                title="Modifier"
                                                            >
                                                                <Edit size={18} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteTeacher(enseignant.id)}
                                                                className="text-red-600 hover:text-red-900 p-2 rounded-full hover:bg-red-100"
                                                                title="Supprimer"
                                                            >
                                                                <Trash2 size={18} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p className="text-gray-600 italic">Aucun enseignant enregistré pour cette école.</p>
                        )}
                    </div>
                )}
                
                {selectedTab === 'classAssignment' && (
                    <div className="bg-white p-6 rounded-lg shadow-lg">
                        <h3 className="text-2xl font-semibold text-gray-800 mb-6">Affectation des Enseignants aux Classes par Année</h3>
                        {(userRole === 'director' || userRole === 'admin' || userRole === 'secretaireGeneral' || userRole === 'superAdmin') && (
                            <div className="bg-gray-50 p-4 rounded-lg shadow-inner mb-6">
                                <form onSubmit={handleAssignmentSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                                    <div>
                                        <label htmlFor="assignTeacher" className="block text-sm font-medium text-gray-700">Enseignant</label>
                                        <select
                                            id="assignTeacher"
                                            value={selectedTeacherForAssignment}
                                            onChange={(e) => setSelectedTeacherForAssignment(e.target.value)}
                                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                                            required
                                        >
                                            <option value="">Sélectionner un enseignant</option>
                                            {enseignants.map(teacher => (
                                                <option key={teacher.id} value={teacher.id}>{teacher.nom} {teacher.prenom}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label htmlFor="assignClass" className="block text-sm font-medium text-gray-700">Classe</label>
                                        <select
                                            id="assignClass"
                                            value={selectedClassForAssignment}
                                            onChange={(e) => setSelectedClassForAssignment(e.target.value)}
                                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                                            required
                                        >
                                            <option value="">Sélectionner une classe</option>
                                            {classes.map(classe => (
                                                <option key={classe.id} value={classe.id}>{classe.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label htmlFor="assignYear" className="block text-sm font-medium text-gray-700">Année Scolaire</label>
                                        <select
                                            id="assignYear"
                                            value={selectedYearForAssignment}
                                            onChange={(e) => setSelectedYearForAssignment(e.target.value)}
                                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                                            required
                                        >
                                            <option value="">Sélectionner une année</option>
                                            {academicYears.map(year => (
                                                <option key={year} value={year}>{year}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-span-full flex justify-end space-x-3">
                                        {isEditingAssignment && (
                                            <button
                                                type="button"
                                                onClick={resetAssignmentForm}
                                                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200"
                                            >
                                                Annuler
                                            </button>
                                        )}
                                        <button
                                            type="submit"
                                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                        >
                                            {isEditingAssignment ? 'Mettre à jour l\'Affectation' : 'Ajouter l\'Affectation'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}
                        <h4 className="text-xl font-semibold text-gray-700 mb-4">Liste des Affectations de Classes ({teacherClassAssignments.length})</h4>
                        {teacherClassAssignments.length > 0 ? (
                            <div className="overflow-x-auto bg-white rounded-lg shadow-md">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Enseignant</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Classe</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Année Scolaire</th>
                                            {(userRole === 'director' || userRole === 'admin' || userRole === 'superAdmin') && (
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {teacherClassAssignments.map(assignment => (
                                            <tr key={assignment.id}>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{getTeacherName(assignment.teacherId)}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{getClassName(assignment.classId)}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{assignment.academicYear}</td>
                                                {(userRole === 'director' || userRole === 'admin' || userRole === 'superAdmin') && (
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                        <div className="flex space-x-2">
                                                            <button
                                                                onClick={() => handleEditAssignment(assignment)}
                                                                className="text-indigo-600 hover:text-indigo-900 p-2 rounded-full hover:bg-indigo-100"
                                                                title="Modifier"
                                                            >
                                                                <Edit size={18} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteAssignment(assignment.id)}
                                                                className="text-red-600 hover:text-red-900 p-2 rounded-full hover:bg-red-100"
                                                                title="Supprimer"
                                                            >
                                                                <Trash2 size={18} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p className="text-gray-600 italic">Aucune affectation de classe enregistrée pour cette école.</p>
                        )}
                    </div>
                )}
                
                {selectedTab === 'subjectAssignment' && (
                    <div className="bg-white p-6 rounded-lg shadow-lg">
                        <h3 className="text-2xl font-semibold text-gray-800 mb-6">Matières Enseignées par Professeur</h3>
                        
                        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-blue-800 text-sm">
                                <strong>💡 Information :</strong> Les matières sont maintenant chargées depuis "matieres_globales" pour être synchronisées avec le module Matières.
                                Seules les matières marquées comme "Actif" sont affichées.
                                {!isFrancoArabe && " Les matières arabes sont masquées pour les écoles non franco-arabes."}
                            </p>
                            {isFrancoArabe && (
                                <p className="text-blue-800 text-sm mt-1">
                                    <strong>Type d'école :</strong> École Franco-Arabe - Affichage des matières françaises, arabes et communes.
                                </p>
                            )}
                            {!isFrancoArabe && (
                                <p className="text-blue-800 text-sm mt-1">
                                    <strong>Type d'école :</strong> École Standard - Affichage des matières françaises et communes uniquement.
                                </p>
                            )}
                        </div>
                        
                        {(userRole === 'director' || userRole === 'admin' || userRole === 'superAdmin') && (
                            <div className="bg-gray-50 p-4 rounded-lg shadow-inner mb-6">
                                <form onSubmit={handleSubjectAssignmentSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                                    <div>
                                        <label htmlFor="assignTeacherSubject" className="block text-sm font-medium text-gray-700">Enseignant</label>
                                        <select
                                            id="assignTeacherSubject"
                                            value={selectedTeacherForSubjectAssignment}
                                            onChange={(e) => setSelectedTeacherForSubjectAssignment(e.target.value)}
                                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                                            required
                                        >
                                            <option value="">Sélectionner un enseignant</option>
                                            {enseignants.map(teacher => (
                                                <option key={teacher.id} value={teacher.id}>{teacher.nom} {teacher.prenom}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label htmlFor="assignSubject" className="block text-sm font-medium text-gray-700">Matière</label>
                                        <select
                                            id="assignSubject"
                                            value={selectedSubjectForAssignment}
                                            onChange={(e) => setSelectedSubjectForAssignment(e.target.value)}
                                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                                            required
                                        >
                                            <option value="">Sélectionner une matière</option>
                                            {subjects.map(subject => {
                                                // Pour les écoles non franco-arabes, masquer complètement les matières arabes
                                                if (!isFrancoArabe && subject.type === 'arabe') {
                                                    return null;
                                                }
                                                
                                                return (
                                                    <option key={subject.id} value={subject.id}>
                                                        {subject.name} 
                                                        {subject.niveau && subject.niveau !== 'tous' ? ` (${subject.niveau})` : ''}
                                                        {isFrancoArabe && subject.type ? ` [${subject.type === 'arabe' ? 'Arabe' : subject.type === 'commun' ? 'Commun' : 'Français'}]` : ''}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </div>
                                    <div>
                                        <label htmlFor="assignYearSubject" className="block text-sm font-medium text-gray-700">Année Scolaire</label>
                                        <select
                                            id="assignYearSubject"
                                            value={selectedYearForSubjectAssignment}
                                            onChange={(e) => setSelectedYearForSubjectAssignment(e.target.value)}
                                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                                            required
                                        >
                                            <option value="">Sélectionner une année</option>
                                            {academicYears.map(year => (
                                                <option key={year} value={year}>{year}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-span-full flex justify-end space-x-3">
                                        {isEditingSubjectAssignment && (
                                            <button
                                                type="button"
                                                onClick={resetSubjectAssignmentForm}
                                                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200"
                                            >
                                                Annuler
                                            </button>
                                        )}
                                        <button
                                            type="submit"
                                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                        >
                                            {isEditingSubjectAssignment ? 'Mettre à jour l\'Affectation' : 'Ajouter l\'Affectation'}
                                        </button>
                                    </div>
                                </form>
                                
                                {isFrancoArabe && (
                                    <div className="mt-2 text-sm text-gray-600">
                                        <p className="mb-1">Légende des types :</p>
                                        <div className="flex space-x-2">
                                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">[Français]</span>
                                            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">[Arabe]</span>
                                            <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">[Commun]</span>
                                        </div>
                                        <p className="mt-2 text-xs">
                                            <strong>Note :</strong> Les matières marquées comme "Inactif" dans MatièresManagement ne sont pas affichées ici.
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                        
                        <h4 className="text-xl font-semibold text-gray-700 mb-4">Liste des Affectations de Matières ({teacherSubjectAssignments.length})</h4>
                        {teacherSubjectAssignments.length > 0 ? (
                            <div className="overflow-x-auto bg-white rounded-lg shadow-md">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Enseignant</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Matière</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Année Scolaire</th>
                                            {(userRole === 'director' || userRole === 'admin' || userRole === 'superAdmin') && (
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {teacherSubjectAssignments.map(assignment => (
                                            <tr key={assignment.id}>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{getTeacherName(assignment.teacherId)}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {getSubjectName(assignment.subjectId)}
                                                    {isFrancoArabe && (() => {
                                                        const subject = subjects.find(s => s.id === assignment.subjectId);
                                                        if (subject && subject.type) {
                                                            const typeLabel = subject.type === 'arabe' ? 'Arabe' : 
                                                                           subject.type === 'commun' ? 'Commun' : 'Français';
                                                            const typeColor = subject.type === 'arabe' ? 'bg-green-100 text-green-800' : 
                                                                            subject.type === 'commun' ? 'bg-purple-100 text-purple-800' : 
                                                                            'bg-blue-100 text-blue-800';
                                                            return <span className={`ml-2 text-xs ${typeColor} px-1 rounded`}>[{typeLabel}]</span>;
                                                        }
                                                        return null;
                                                    })()}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{assignment.academicYear}</td>
                                                {(userRole === 'director' || userRole === 'admin' || userRole === 'superAdmin') && (
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                        <div className="flex space-x-2">
                                                            <button
                                                                onClick={() => handleEditSubjectAssignment(assignment)}
                                                                className="text-indigo-600 hover:text-indigo-900 p-2 rounded-full hover:bg-indigo-100"
                                                                title="Modifier"
                                                            >
                                                                <Edit size={18} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteSubjectAssignment(assignment.id)}
                                                                className="text-red-600 hover:text-red-900 p-2 rounded-full hover:bg-red-100"
                                                                title="Supprimer"
                                                            >
                                                                <Trash2 size={18} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p className="text-gray-600 italic">Aucune affectation de matière enregistrée pour cette école.</p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default EnseignantsManagement;